// Småt-gæsterne: en stork, en drage, en ko og et juletræ, der sætter sig på ledningen. Tegnet som
// resten af atlasset (blød kontur, brandfarver, ingen rød). Fod i (0,0): der sidder de på tråden.

import type { Graphics } from 'pixi.js';
import { C } from './palette';

const OUT = C.ink;
const OW = 2;

type Draw = (g: Graphics) => void;

function eye(g: Graphics, x: number, y: number, s = 1): void {
  g.circle(x, y, 1.9 * s).fill(OUT);
  g.circle(x + 0.6 * s, y - 0.7 * s, 0.7 * s).fill(C.white);
}

/** Storken: hvid krop, sorte vingespidser, lange orange ben og næb. Står på ét ben. */
const stork: Draw = (g) => {
  g.moveTo(-1, 0).lineTo(-1, -9).stroke({ width: 1.8, color: C.orange, cap: 'round' });
  g.moveTo(-1, -6).lineTo(3, -9).stroke({ width: 1.6, color: C.orange, cap: 'round' });
  g.ellipse(0, -14, 10, 6).fill(C.white).stroke({ width: OW, color: OUT });
  g.poly([-10, -14, -3, -10, -12, -9], true).fill(OUT);
  g.moveTo(6, -17).quadraticCurveTo(9, -23, 7, -28).stroke({ width: 4.2, color: OUT, cap: 'round' });
  g.moveTo(6, -17).quadraticCurveTo(9, -23, 7, -28).stroke({ width: 2.2, color: C.white, cap: 'round' });
  g.circle(8, -29, 4.2).fill(C.white).stroke({ width: OW, color: OUT });
  g.poly([11, -30, 21, -28, 11, -27], true).fill(C.orange).stroke({ width: 1.2, color: OUT, join: 'round' });
  eye(g, 8.6, -30, 0.9);
};

/** Dragen: en turkis rombe med gul hale, der har viklet sig om tråden. */
const drage: Draw = (g) => {
  g.moveTo(0, -4).quadraticCurveTo(-6, 2, -2, 6).quadraticCurveTo(4, 9, 1, 13).stroke({ width: 1.4, color: OUT, cap: 'round' });
  for (const [x, y] of [
    [-4, 2],
    [2, 8],
  ] as const) {
    g.poly([x - 3, y - 2, x + 3, y + 2, x - 3, y + 2], true).fill(C.yellow).stroke({ width: 1, color: OUT });
  }
  g.poly([0, -30, 11, -17, 0, -4, -11, -17], true).fill(C.turquoise).stroke({ width: OW, color: OUT, join: 'round' });
  g.poly([0, -30, 11, -17, 0, -17], true).fill({ color: C.white, alpha: 0.35 });
  g.moveTo(0, -30).lineTo(0, -4).moveTo(-11, -17).lineTo(11, -17).stroke({ width: 1.1, color: OUT, alpha: 0.6 });
  eye(g, -3.5, -19);
  eye(g, 3.5, -19);
  g.moveTo(-2.5, -13).quadraticCurveTo(0, -11, 2.5, -13).stroke({ width: 1.4, color: OUT, cap: 'round' });
};

/** Koen: sidder overskrævs på tråden med benene ned på hver side. */
const ko: Draw = (g) => {
  for (const x of [-7, 6]) {
    g.roundRect(x - 1.8, -4, 3.6, 9, 1.5).fill(C.white).stroke({ width: 1.4, color: OUT });
    g.rect(x - 1.8, 3, 3.6, 2).fill(OUT);
  }
  g.ellipse(0, -9, 12, 7.5).fill(C.white).stroke({ width: OW, color: OUT });
  g.ellipse(-4, -10, 4, 3).fill(OUT);
  g.ellipse(5, -6, 3, 2.2).fill(OUT);
  g.ellipse(11, -17, 6.5, 5.5).fill(C.white).stroke({ width: OW, color: OUT });
  g.ellipse(14.5, -15, 3.6, 2.8).fill(C.skin).stroke({ width: 1.2, color: OUT });
  g.moveTo(7, -22).lineTo(5, -25).moveTo(14, -22).lineTo(16, -25).stroke({ width: 1.8, color: OUT, cap: 'round' });
  eye(g, 9.5, -18.5, 0.9);
  eye(g, 13, -18.5, 0.9);
  g.moveTo(-12, -9).quadraticCurveTo(-16, -6, -15, -1).stroke({ width: 1.4, color: OUT, cap: 'round' });
};

/** Juletræet: grønt træ med gul stjerne og kugler, der hænger på tråden i sin fod. */
const juletrae: Draw = (g) => {
  g.rect(-2, -4, 4, 5).fill(C.wood).stroke({ width: 1.2, color: OUT });
  g.poly([0, -32, 9, -18, 5, -18, 12, -5, -12, -5, -5, -18, -9, -18], true).fill(C.green).stroke({ width: OW, color: OUT, join: 'round' });
  for (const [x, y, c] of [
    [-4, -9, C.yellow],
    [5, -12, C.white],
    [0, -21, C.orange],
    [-6, -16, C.turquoise],
  ] as const) {
    g.circle(x, y, 1.9).fill(c).stroke({ width: 0.8, color: OUT });
  }
  g.star(0, -33, 5, 4.5, 2).fill(C.yellow).stroke({ width: 1.2, color: OUT, join: 'round' });
};

export const SMAA_DRAWS: Record<string, Draw> = {
  smaa_stork: stork,
  smaa_drage: drage,
  smaa_ko: ko,
  smaa_juletrae: juletrae,
};
